<script setup lang="ts">
import { onMounted, computed, ref, onUnmounted, watch } from 'vue'
import Swiper from 'swiper'
import MinimalHeader from '@/components/MinimalHeader.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { fetchBrands } from '@/assets/ts/apiManagement'
import { type IBrand } from '@/types/Brands'
import { useBreakpoints, getCurrentGaPageName } from '@/lib/utils'
import { type InvitePageContent } from '@/assets/ts/pages/invite'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import { isApp } from '@/assets/ts/checkApp'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

const { isTabletPortrait } = useBreakpoints()

const props = defineProps<{
	content: InvitePageContent
}>()

const rewardProcessIcons = ['like_hollow', 'edit', 'gift_v3']
const valuePropositionIcons = ['price_v3', 'community_v3', 'warehouse_v3']
const brandsData = ref<
	{
		country_id: string
		country_title: string
		brands_count: number
		brands: IBrand[]
	}[]
>([])

const swiperEl = ref<HTMLDivElement | null>(null)
let swiper: Swiper | null = null
const firstSlideEl = ref<HTMLElement | null>(null)
const isFirstSlideVisible = ref(true)
const isShowLeftShadow = computed(
	() => !isFirstSlideVisible.value && isTabletPortrait.value,
)
const lastSlideEl = ref<HTMLElement | null>(null)
const isLastSlideVisible = ref(true)
const isShowRightShadow = computed(
	() => !isLastSlideVisible.value && isTabletPortrait.value,
)
const isAllSlidesVisible = computed(() => {
	return isFirstSlideVisible.value && isLastSlideVisible.value
})

const countryIconMap: Record<string, { icon: string; bgColor: string }> = {
	us: {
		icon: 'region_outline_us',
		bgColor: 'bg-t-700',
	},
	jp: {
		icon: 'region_outline_jp',
		bgColor: 'bg-o-500',
	},
	kr: {
		icon: 'region_outline_kr',
		bgColor: 'bg-y-400',
	},
	hk: {
		icon: 'region_outline_hk',
		bgColor: 'bg-t-500',
	},
	ca: {
		icon: 'region_outline_ca',
		bgColor: 'bg-r-700',
	},
}

const showSignupPanel = (from: 'hero' | 'bottom') => {
	const buttonName = from === 'hero' ? 'Sign Up - Hero' : 'Get Start'

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: getCurrentGaPageName(),
		button_name: buttonName,
	})

	if (isApp) {
		window.location.href = `${getSiteBaseUrl()}/login`
	} else {
		window.BsHeaderVm.showAuthPanel()
	}
}

const titleWithCredits = computed(() => {
	const welcomeCredit = window.BUYANDSHIP.settings.member_invite_host_credits
	return props.content.heroContent.title.replace(
		'{WELCOME_CREDIT}',
		String(welcomeCredit),
	)
})

const getAvailBrandsText = (brandsLength: number) => {
	return props.content.otherInformationContent.availBrandsText.replace(
		'{BRANDS_NUM}',
		String(brandsLength),
	)
}

const fetchBrandsData = async () => {
	try {
		const { content } = await fetchBrands()

		const MAX_COUNTRIES = 5
		const featuredCountries = content.countries.slice(0, MAX_COUNTRIES)

		brandsData.value = featuredCountries.map(country => {
			const MAX_BRANDS = 4
			const countryBrands = content.brands.filter(
				brand => brand.country_id === country.id,
			)
			const featuredBrands = countryBrands.slice(0, MAX_BRANDS)

			return {
				country_id: country.id,
				country_title: country.title,
				brands_count: countryBrands.length,
				brands: featuredBrands,
			}
		})
	} catch (error) {
		console.warn(error)
	}
}

const swiperOptions = {
	slidesPerView: 'auto' as const,
	spaceBetween: 20,
	speed: 500,
	centeredSlides: true,
	centeredSlidesBounds: true,
	centerInsufficientSlides: true,
	grabCursor: true,
}

const disableSwiperTouch = () => {
	if (swiper === null) return

	swiper.allowTouchMove = false
	swiper.unsetGrabCursor()
}

const enableSwiperTouch = () => {
	if (swiper === null) return

	swiper.allowTouchMove = true
	swiper.setGrabCursor()
}

const initializeSwiper = () => {
	if (!swiperEl.value) return

	swiper = new Swiper(swiperEl.value, swiperOptions)
	swiper.slideTo(swiper.slides.length / 2)

	if (isAllSlidesVisible.value) {
		disableSwiperTouch()
	}
}

let firstSlideObserver: IntersectionObserver | null = null
let lastSlideObserver: IntersectionObserver | null = null

const initializeObservers = () => {
	firstSlideObserver = new IntersectionObserver(
		([entry]) => {
			isFirstSlideVisible.value = entry.isIntersecting
		},
		{ threshold: 1, rootMargin: '100% 0px' },
	)

	lastSlideObserver = new IntersectionObserver(
		([entry]) => {
			isLastSlideVisible.value = entry.isIntersecting
		},
		{ threshold: 1, rootMargin: '100% 0px' },
	)

	if (firstSlideEl.value) firstSlideObserver.observe(firstSlideEl.value)
	if (lastSlideEl.value) lastSlideObserver.observe(lastSlideEl.value)
}

watch(isAllSlidesVisible, () => {
	if (swiper === null) return

	// Prevent swiper from being scrolled when all slides are visible
	if (isAllSlidesVisible.value) {
		disableSwiperTouch()
	} else {
		enableSwiperTouch()
	}
})

onMounted(async () => {
	await fetchBrandsData()

	initializeSwiper()
	initializeObservers()
})

onUnmounted(() => {
	swiper?.destroy()
	firstSlideObserver?.disconnect()
	lastSlideObserver?.disconnect()
})
</script>

<template>
	<div class="text-n-950">
		<MinimalHeader v-if="!isApp" />

		<section class="overflow-hidden bg-o-50">
			<div class="bs-container">
				<div
					class="col-span-12 grid grid-cols-1 items-center gap-20px py-acquaintances text-center mdp:grid-cols-2 mdp:text-start"
				>
					<div class="relative flex justify-center">
						<div
							class="hero-bg absolute -top-[242px] left-1/2 h-[466px] w-[326px] -translate-x-1/2 bg-cover bg-center mdp:-top-[99px] mdp:h-[490px] mdp:w-[342px] mdl:-top-[121px] lg:-top-[184px] lg:h-[670px] lg:w-[467px]"
						></div>

						<div
							class="hero-image relative h-[224px] w-[297px] bg-cover bg-center mdp:h-[248px] mdp:w-[328px] lg:h-[302px] lg:w-[400px]"
						></div>
					</div>

					<div>
						<div class="typography-b1-reg mb-close-ish-friends">
							{{ content.heroContent.description }}
						</div>

						<div
							class="typography-h1-str mb-good-friends [&>span]:text-o-500"
							v-html="titleWithCredits"
						/>

						<BaseButton
							size="large"
							class="w-full mdp:w-max"
							@click="showSignupPanel('hero')"
						>
							{{ content.heroContent.ctaButton }}
						</BaseButton>
					</div>
				</div>
			</div>
		</section>

		<section class="bs-container py-acquaintances">
			<div class="col-span-12">
				<ol
					class="grid list-decimal gap-24px mdp:grid-flow-col mdp:grid-cols-3 mdp:gap-20px lg:grid-cols-[calc((100%-120px)/3)_calc((100%-120px)/3+40px)_calc((100%-120px)/3+40px)]"
				>
					<div
						v-for="(step, index) in content.rewardProcessContent.steps"
						:key="index"
						class="flex gap-16px"
					>
						<div v-if="index !== 0" class="mr-4px hidden items-center lg:flex">
							<BaseIcon name="chevron_thin" class="h-20px w-20px fill-n-950" />
						</div>

						<div
							class="grid h-[36px] w-[36px] flex-shrink-0 place-content-center rounded-full bg-o-50 p-8px"
						>
							<BaseIcon
								:name="rewardProcessIcons[index]"
								class="h-20px w-20px fill-o-500"
							/>
						</div>

						<li class="typography-h5-str ml-20px">
							<span>
								{{ step.title }}
							</span>

							<div class="typography-b2-reg -ml-20px mt-8px text-n-400">
								{{ step.description }}
							</div>
						</li>
					</div>
				</ol>
			</div>
		</section>

		<section class="bs-container py-good-friends">
			<div
				class="relative col-span-12 grid grid-cols-1 gap-good-friends overflow-hidden rounded-[24px] bg-o-75 px-24px py-casual-friends mdp:grid-cols-2 mdp:items-center mdp:px-60px"
			>
				<div
					class="value-proposition-bg absolute -top-[121px] left-[22%] aspect-square w-[369px] bg-cover bg-center mdp:-top-[300px] mdp:left-[42%] mdp:w-[840px] mdl:-top-[324px] mdl:left-[31%] lg:-top-[336px] lg:left-[33%]"
				></div>

				<div class="typography-h3-str relative">
					{{ content.valuePropositionContent.title }}
				</div>

				<div class="relative flex flex-col gap-close-friends">
					<div
						v-for="(item, index) in content.valuePropositionContent.items"
						:key="index"
						class="flex items-center gap-16px"
					>
						<BaseIcon
							:name="valuePropositionIcons[index]"
							class="h-24px w-24px flex-shrink-0 fill-o-500"
						/>

						<div class="typography-s1-reg">
							{{ item.title }}
						</div>
					</div>
				</div>
			</div>
		</section>

		<section v-if="brandsData.length > 0" class="bs-container py-acquaintances">
			<div class="relative col-span-12">
				<div
					v-show="isShowLeftShadow"
					class="absolute left-0 top-0 z-10 h-full w-48px bg-black [background:linear-gradient(270deg,rgba(255,255,255,0)_0%,#FFF_100%)]"
				></div>

				<div
					v-show="isShowRightShadow"
					class="absolute right-0 top-0 z-10 h-full w-48px rotate-180 transform [background:linear-gradient(270deg,rgba(255,255,255,0)_0%,#FFF_100%)]"
				></div>

				<div
					ref="swiperEl"
					class="swiper -mx-20px overflow-hidden px-20px pb-casual-friends mdp:mx-0 mdp:px-0"
				>
					<div class="swiper-wrapper flex">
						<div
							v-for="(brand, index) in brandsData"
							:key="brand.country_id"
							:ref="
								el => {
									if (index === 0) firstSlideEl = el as HTMLElement
									if (index === brandsData.length - 1)
										lastSlideEl = el as HTMLElement
								}
							"
							class="swiper-slide h-[316px] w-[280px] flex-shrink-0 select-none rounded-[24px] border border-n-75 bg-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)]"
						>
							<div class="flex flex-shrink-0 items-center gap-16px p-24px">
								<div
									class="h-max rounded-[12px] p-12px"
									:class="countryIconMap[brand.country_id]?.bgColor"
								>
									<BaseIcon
										:name="countryIconMap[brand.country_id]?.icon"
										class="h-24px w-24px [&_path]:fill-white"
									/>
								</div>

								<div>
									<div class="typography-h5-str mb-4px">
										{{ brand.country_title }}
									</div>

									<div class="typography-s2-reg">
										{{ getAvailBrandsText(brand.brands_count) }}
									</div>
								</div>
							</div>

							<div
								class="grid grid-cols-2 gap-x-16px gap-y-8px px-32px py-16px"
							>
								<a
									v-for="item in brand.brands"
									:key="item.link_url"
									:href="item.link_url"
									target="_blank"
									class="h-[90px] w-[96px] px-[3px]"
								>
									<img
										:src="item.logo_url"
										:alt="`${item.title} logo`"
										class="h-full w-full object-contain"
									/>
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				class="col-span-12 -mt-16px flex flex-col items-center justify-center gap-casual-friends mdl:-mt-20px"
			>
				<div class="typography-s1-reg text-n-500">
					{{ content.otherInformationContent.signUpText }}
				</div>

				<BaseButton size="large" @click="showSignupPanel('bottom')">
					{{ content.otherInformationContent.ctaButton }}
				</BaseButton>
			</div>
		</section>
	</div>
</template>
